import React, { useEffect, useState } from 'react';
import { getAgencies, approveAgency, deleteAgency, setFeaturedAgency, getUserSubscriptionStatus } from '../../services/firestoreService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import PageHead from '../../components/common/PageHead';
import AgencyTable from '../../components/common/AgencyTable';

const ViewAgencies = () => {
  const [agencies, setAgencies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [approvalStatus, setApprovalStatus] = useState(''); 
  const [categoryFilter, setCategoryFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchAgencies = async () => {
      const data = await getAgencies();
      const updatedData = await Promise.all(
        data.map(async (agency) => {
          if (!agency.userId) return { ...agency, isFeaturable: false };
          const subscriptionStatus = await getUserSubscriptionStatus(agency.userId);
          return { ...agency, isFeaturable: subscriptionStatus === "premium" };
        })
      );
      setAgencies(updatedData);
    };

    fetchAgencies();
  }, []);

  const handleApprove = async (agencyId) => {
    await approveAgency(agencyId);
    setAgencies((prev) =>
      prev.map((a) => (a.id === agencyId ? { ...a, isApproved: !a.isApproved } : a))
    );
  };

  const handleDelete = async (agencyId) => {
    if (window.confirm('Are you sure you want to delete this agency?')) {
      await deleteAgency(agencyId);
      setAgencies((prev) => prev.filter((a) => a.id !== agencyId));
    }
  };

  const handleSetFeatured = async (agencyId, isFeaturable) => {
    if (!isFeaturable) {
      alert("This user cannot feature an agency. Upgrade to premium.");
      return;
    }
    await setFeaturedAgency(agencyId);
    setAgencies((prev) =>
      prev.map((a) =>
        a.id === agencyId ? { ...a, isFeatured: !a.isFeatured } : a
      )
    );
  };

  const filteredAgencies = agencies
    .filter((agency) => agency.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter((agency) =>
      approvalStatus === '' || 
      (approvalStatus === 'Approved' && agency.isApproved) || 
      (approvalStatus === 'Pending' && !agency.isApproved)
    )
    .filter((agency) => categoryFilter === '' || agency.category === categoryFilter)
    .filter((agency) => {
      if (startDate && endDate) {
        const agencyDate = agency.dateCreated.toDate();
        return agencyDate >= new Date(startDate) && agencyDate <= new Date(endDate);
      }
      return true;
    });

  const totalPages = Math.ceil(filteredAgencies.length / itemsPerPage);
  const indexOfLastAgency = currentPage * itemsPerPage;
  const indexOfFirstAgency = indexOfLastAgency - itemsPerPage;
  const currentAgencies = filteredAgencies.slice(indexOfFirstAgency, indexOfLastAgency);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  
  return (
    <>
      <PageHead name='Agency Management' />
      <div className="container mx-auto p-4">
        
        <div className="flex flex-col md:flex-row justify-between my-2 items-start md:items-center space-y-1 md:space-y-0 md:space-x-3">
          <input
            type="text"
            placeholder="Search by name"
            className="w-full px-4 py-2 border border-primary rounded-md shadow-lg focus:outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FontAwesomeIcon icon={faSearch} className="text-xl text-primary hidden sm:block" />

          <select
            className="w-full md:w-48 px-4 py-2 border border-primary rounded-md"
            value={approvalStatus}
            onChange={(e) => setApprovalStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="Approved">Approved</option>
            <option value="Pending">Pending</option>
          </select>

          <select
            className="w-full md:w-48 px-4 py-2 border border-primary rounded-md"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Finance">Finance</option>
            <option value="Technology">Technology</option>
          </select>
        </div>

        <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-3">
          <div>
            <label>From</label>
            <input
              type="date"
              className="w-full px-4 py-2 border border-primary rounded-md"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label>To</label>
            <input
              type="date"
              className="w-full px-4 py-2 border border-primary rounded-md"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <AgencyTable
          agencies={currentAgencies}
          showIndex={true}
          onApprove={handleApprove}
          onDelete={handleDelete}
          onFeature={handleSetFeatured}
          allowFeature={true}
          startIndex={indexOfFirstAgency}
        />

        <div className="flex justify-center mt-4">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index + 1}
              onClick={() => handlePageChange(index + 1)}
              className={`mx-1 py-1 px-3 font-bold rounded ${
                currentPage === index + 1 ? 'underline text-secondary' : 'text-primary'
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default ViewAgencies;
